import React from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage, useT } from "@/lib/LanguageContext";
import { adminHref } from "../../adminPath";
import { OFFERING_TYPE_KEYS } from "../contract";
import { crmHref } from "../paths";
import { fmtCount } from "../format";
import useCrmMoney from "../hooks/useCrmMoney";
import CrmCard from "../ui/CrmCard";
import { CRM } from "../../../constants/testIds/crm";

/** Where a top-offering row opens (spec: "Row click opens the offering"). */
const offeringHref = (row) => {
  if (row.type === "course") return `${adminHref("/admin/learn")}?course=${encodeURIComponent(row.id)}`;
  if (row.type === "product") return `${adminHref("/admin/products")}?edit=${encodeURIComponent(row.id)}`;
  if (row.type === "pack") return `${crmHref("/packs")}?edit=${encodeURIComponent(row.id)}`;
  if (row.type === "session") return adminHref("/admin/booking-services");
  return null;
};

/** "Top offerings" — five rows: name, type, units, revenue; "—" where there is none. */
export default function TopOfferingsTable({ rows = [], currency }) {
  const t = useT("crm");
  const { lang } = useLanguage();
  const money = useCrmMoney();
  const navigate = useNavigate();
  return (
    <CrmCard data-testid={CRM.perfTop}>
      <h3 className="mb-2 font-display text-xl text-eh-ink">{t("perf.topTitle")}</h3>
      {rows.length === 0 ? (
        <p className="py-4 text-sm text-eh-muted">{t("perf.topEmpty")}</p>
      ) : (
        <div className="overflow-x-auto" data-lenis-prevent>
          <table className="w-full min-w-[480px] text-sm text-eh-ink">
            <thead>
              <tr className="border-b border-eh-border-soft text-left text-xs uppercase tracking-wider text-eh-muted">
                <th scope="col" className="py-2 px-2 font-semibold">{t("perf.colName")}</th>
                <th scope="col" className="py-2 px-2 font-semibold">{t("perf.colType")}</th>
                <th scope="col" className="py-2 px-2 font-semibold">{t("perf.colUnits")}</th>
                <th scope="col" className="py-2 px-2 font-semibold">{t("perf.colRevenue")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 5).map((row, i) => {
                const href = offeringHref(row);
                return (
                  <tr key={`${row.type}-${row.id}`} data-testid={CRM.perfTopRow(i)}
                    onClick={href ? () => navigate(href) : undefined}
                    className={`border-b border-eh-border-soft ${href ? "cursor-pointer hover:bg-eh-cream-deep" : ""}`}>
                    <td className="py-3 px-2">
                      {href
                        ? <a href={href} onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(href); }} className="hover:underline">{row.name}</a>
                        : row.name}
                    </td>
                    <td className="py-3 px-2">{OFFERING_TYPE_KEYS[row.type] ? t(OFFERING_TYPE_KEYS[row.type]) : "—"}</td>
                    <td className="py-3 px-2">{row.units === null || row.units === undefined ? "—" : fmtCount(row.units, lang)}</td>
                    <td className="py-3 px-2">{money(row.revenue_cents, row.currency || currency)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </CrmCard>
  );
}
